import { __decorate } from "tslib";
import React from 'react';
import PropTypes from 'prop-types';
import { action, toJS } from 'mobx';
import { observer } from 'mobx-react';
import noop from 'lodash/noop';
import { getTooltipTheme } from 'choerodon-ui/lib/_util/TooltipUtils';
import { FormField } from '../field/FormField';
import autobind from '../_util/autobind';
import { ViewMode } from './enum';
import { $l } from '../locale-context';
import { LabelLayout } from '../form/enum';
import { Tooltip as TextTooltip } from '../core/enum';
import isOverflow from '../overflow-tip/util';
import { show } from '../tooltip/singleton';
export class Radio extends FormField {
    constructor() {
        super(...arguments);
        this.type = 'radio';
    }
    get defaultValidationMessages() {
        const label = this.getProp('label');
        return {
            valueMissing: $l('Radio', label ? 'value_missing' : 'value_missing_no_label', { label }),
        };
    }
    get checkedValue() {
        const { value = 'on' } = this.props;
        return value;
    }
    saveLabelRef(node) {
        this.labelRef = node;
    }
    getControlled(props) {
        return props.checked !== undefined;
    }
    getOmitPropsKeys() {
        return super.getOmitPropsKeys().concat([
            'value',
            'readOnly',
            'mode',
        ]);
    }
    getOtherProps() {
        const otherProps = super.getOtherProps();
        otherProps.type = this.type;
        otherProps.onMouseDown = this.handleMouseDown;
        otherProps.onClick = otherProps.onChange;
        otherProps.onChange = noop;
        return otherProps;
    }
    showTooltip(e) {
        if (super.showTooltip(e)) {
            return true;
        }
        const { labelTooltip, labelRef } = this;
        if (labelRef && (labelTooltip === TextTooltip.always || (labelTooltip === TextTooltip.overflow && isOverflow(labelRef)))) {
            const labelText = this.getLabelText();
            if (labelText) {
                show(labelRef, {
                    title: labelText,
                    theme: getTooltipTheme('label'),
                });
                return true;
            }
        }
        return false;
    }
    renderWrapper() {
        const checked = this.isChecked();
        const floatLabel = super.hasFloatLabel ? this.renderSwitchFloatLabel() : undefined;
        return (React.createElement(React.Fragment, null,
            React.createElement("label", Object.assign({ key: "wrapper" }, this.getWrapperProps()),
                React.createElement("input", Object.assign({}, this.getOtherProps(), { checked: checked, value: this.checkedValue })),
                this.renderInner(),
                this.getTextNode(),
                this.renderFloatLabel()),
            floatLabel));
    }
    /**
     * 解决form 在float的时候没有表头的问题
     * 也可以在需要不在组件内部展现label的时候使用
     */
    renderSwitchFloatLabel() {
        return undefined;
    }
    renderInner() {
        return React.createElement("span", { className: `${this.prefixCls}-inner` });
    }
    /**
     * 当使用label代替children时，不需要展示float label
     *
     * @readonly
     * @memberof Radio
     */
    get hasFloatLabel() {
        return this.getLabelChildren() ? false : super.hasFloatLabel;
    }
    /**
     * 没有children时，使用label替代children
     *
     * @returns {ReactNode} label
     * @memberof Radio
     */
    getLabelChildren() {
        const { labelLayout } = this;
        return (labelLayout &&
            ![LabelLayout.horizontal, LabelLayout.vertical, LabelLayout.none].includes(labelLayout) &&
            this.getLabel());
    }
    getChildrenText() {
        return toJS(this.props.children);
    }
    getLabelText() {
        return this.getChildrenText() || this.getLabelChildren();
    }
    getTextNode() {
        const { prefixCls } = this;
        const text = this.getLabelText();
        if (text) {
            return React.createElement("span", { ref: this.saveLabelRef, className: `${prefixCls}-label` }, text);
        }
    }
    getWrapperClassNames(...args) {
        const { prefixCls, props: { mode }, } = this;
        return super.getWrapperClassNames({
            [`${prefixCls}-button`]: mode === ViewMode.button,
        }, ...args);
    }
    isChecked() {
        const { checked } = this.props;
        const { name, dataSet, checkedValue } = this;
        if (dataSet && name) {
            return this.getDataSetValue() === checkedValue;
        }
        return checked;
    }
    handleMouseDown(e) {
        // e.stopPropagation();
        const { onMouseDown } = this.props;
        if (typeof onMouseDown === 'function') {
            onMouseDown(e);
        }
    }
    handleChange(e) {
        const { onClick = noop } = this.props;
        const { checked } = e.target;
        onClick(e);
        this.setChecked(checked);
    }
    setChecked(checked) {
        if (checked) {
            this.setValue(this.checkedValue);
        }
    }
    getOldValue() {
        return this.isChecked() ? this.checkedValue : undefined;
    }
}
Radio.displayName = 'Radio';
Radio.propTypes = {
    /**
     * <受控>是否选中
     */
    checked: PropTypes.bool,
    /**
     * 初始是否选中
     */
    defaultChecked: PropTypes.bool,
    /**
     * 显示模式
     * 可选值： button | box
     * @default box
     */
    mode: PropTypes.oneOf([ViewMode.button, ViewMode.box]),
    ...FormField.propTypes,
};
Radio.defaultProps = {
    ...FormField.defaultProps,
    suffixCls: 'radio',
};
// eslint-disable-next-line camelcase
Radio.__PRO_RADIO = true;
// eslint-disable-next-line camelcase
Radio.__IS_IN_CELL_EDITOR = true;
__decorate([
    autobind
], Radio.prototype, "saveLabelRef", null);
__decorate([
    autobind
], Radio.prototype, "handleMouseDown", null);
__decorate([
    autobind
], Radio.prototype, "handleChange", null);
__decorate([
    action
], Radio.prototype, "setChecked", null);
let ObserverRadio = class ObserverRadio extends Radio {
};
ObserverRadio.defaultProps = Radio.defaultProps;
// eslint-disable-next-line camelcase
ObserverRadio.__PRO_RADIO = true;
// eslint-disable-next-line camelcase
ObserverRadio.__IS_IN_CELL_EDITOR = true;
ObserverRadio = __decorate([
    observer
], ObserverRadio);
export default ObserverRadio;
